import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Alert,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const ProjectEditor = ({ brandId, brandSlug }) => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const getAuthHeaders = (isJson = true) => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (isJson) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  };

  useEffect(() => {
    fetchProjects();
  }, [brandId]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/storefront/projects?brandId=${brandId}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setProjects(data.projects || []);

      if (data.projects && data.projects.length > 0 && !selectedProjectId) {
        setSelectedProjectId(data.projects[0].id);
        setFormData(data.projects[0]);
      }
    } catch (error) {
      toast.error('Failed to load projects');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProjectId && projects.length > 0) {
      const project = projects.find(p => p.id === selectedProjectId);
      if (project) {
        setFormData({ ...project });
        setHasChanges(false);
      }
    }
  }, [selectedProjectId, projects]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => {
      const updated = { ...prev };
      updated[field][index] = value;
      return updated;
    });
    setHasChanges(true);
  };

  const handleAddArrayItem = (field, defaultValue) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), defaultValue]
    }));
    setHasChanges(true);
  };

  const handleRemoveArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleStatChange = (index, field, value) => {
    setFormData(prev => {
      const updated = { ...prev };
      updated.impactStats[index][field] = value;
      return updated;
    });
    setHasChanges(true);
  };

  const handleVideoChange = (index, field, value) => {
    setFormData(prev => {
      const updated = { ...prev };
      updated.videos[index][field] = value;
      return updated;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      const response = await fetch('/api/storefront/projects/save', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          brandId,
          projectId: selectedProjectId,
          project: formData
        }),
      });

      if (response.ok) {
        toast.success('Project saved successfully');
        setHasChanges(false);
        fetchProjects();
      } else {
        toast.error('Failed to save project');
      }
    } catch (error) {
      toast.error('Error saving project');
      console.error(error);
    }
  };

  const handleCreateProject = async () => {
    try {
      const newProject = {
        id: `project_${Date.now()}`,
        name: 'New Project',
        slug: `project-${Date.now()}`,
        type: 'Collaborative Project',
        mission: '',
        coreFocus: [],
        impactStats: [],
        videos: [],
        ctaText: 'Learn More',
        ctaLink: '',
        published: false,
        isDefault: false
      };

      const response = await fetch('/api/storefront/projects/create', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          brandId,
          project: newProject
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Project created');
        fetchProjects();
        setSelectedProjectId(data.projectId);
      } else {
        toast.error('Failed to create project');
      }
    } catch (error) {
      toast.error('Error creating project');
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      const response = await fetch('/api/storefront/projects/delete', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          brandId,
          projectId: selectedProjectId
        }),
      });

      if (response.ok) {
        toast.success('Project deleted');
        setSelectedProjectId(null);
        fetchProjects();
      } else {
        toast.error('Failed to delete project');
      }
    } catch (error) {
      toast.error('Error deleting project');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading projects...</Typography>
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No projects yet. Create your first project to get started.
        </Alert>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateProject}
        >
          Create First Project
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Select Project</InputLabel>
          <Select
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            label="Select Project"
          >
            {projects.map(project => (
              <MenuItem key={project.id} value={project.id}>
                {project.name} {project.isDefault && '(Default)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateProject}
          >
            New Project
          </Button>
          {!formData.isDefault && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges}
          >
            {hasChanges ? 'Save Changes' : 'Saved'}
          </Button>
        </Box>
      </Box>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have unsaved changes
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Project Name"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Slug (URL path)"
                    value={formData.slug || ''}
                    onChange={(e) => handleChange('slug', e.target.value)}
                    helperText={`URL will be: /projects/${formData.slug}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Project Type"
                    value={formData.type || ''}
                    onChange={(e) => handleChange('type', e.target.value)}
                    placeholder="e.g., Collaborative Project"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mission Statement"
                    multiline
                    rows={4}
                    value={formData.mission || ''}
                    onChange={(e) => handleChange('mission', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Core Focus Areas */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Core Focus Areas
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddArrayItem('coreFocus', '')}
                >
                  Add Focus Area
                </Button>
              </Box>

              {(formData.coreFocus || []).map((item, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={item}
                    onChange={(e) => handleArrayChange('coreFocus', index, e.target.value)}
                    placeholder="Focus area..."
                  />
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleRemoveArrayItem('coreFocus', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              {(!formData.coreFocus || formData.coreFocus.length === 0) && (
                <Alert severity="info">
                  No focus areas added yet
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Impact Stats */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Impact Statistics
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddArrayItem('impactStats', { label: '', value: '' })}
                >
                  Add Stat
                </Button>
              </Box>

              {(formData.impactStats || []).map((stat, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    value={stat.value}
                    onChange={(e) => handleStatChange(index, 'value', e.target.value)}
                    placeholder="Value (e.g., 500+)"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    value={stat.label}
                    onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                    placeholder="Label (e.g., Students)"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleRemoveArrayItem('impactStats', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              {(!formData.impactStats || formData.impactStats.length === 0) && (
                <Alert severity="info">
                  No impact stats added yet
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Videos */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Videos
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddArrayItem('videos', { id: Date.now(), url: '', thumbnail: '' })}
                >
                  Add Video
                </Button>
              </Box>

              {(formData.videos || []).map((video, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">Video {index + 1}</Typography>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveArrayItem('videos', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Video URL"
                      value={video.url || ''}
                      onChange={(e) => handleVideoChange(index, 'url', e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Thumbnail URL (optional)"
                      value={video.thumbnail || ''}
                      onChange={(e) => handleVideoChange(index, 'thumbnail', e.target.value)}
                    />
                  </CardContent>
                </Card>
              ))}

              {(!formData.videos || formData.videos.length === 0) && (
                <Alert severity="info">
                  No videos added yet
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Call to Action */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Call to Action
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Button Text"
                    value={formData.ctaText || ''}
                    onChange={(e) => handleChange('ctaText', e.target.value)}
                    placeholder="e.g., Learn More"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Link URL"
                    value={formData.ctaLink || ''}
                    onChange={(e) => handleChange('ctaLink', e.target.value)}
                    placeholder="https://..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Published Status */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.published || false}
                    onChange={(e) => handleChange('published', e.target.checked)}
                  />
                }
                label="Published (visible on site)"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectEditor;
